import { useGlobals } from '@/hooks';
import * as S from './styles';
import React, { useState, useRef, useEffect, TouchEvent, MouseEvent } from 'react';

interface Props {
  id: string;
  children: any;
  isOpen: boolean;
  shadown?: boolean;
  isLoading?: boolean;
  fullHeight?: boolean;
  direction?: 'bottom' | 'right';
  bottomMargin?: number;
  headerIcon?: React.ReactNode;
  isScrollable?: boolean;
  dragHeaderColor?: string;
  showCloseIcon?: boolean;
  showDragHeader?: boolean;
  closeModal: (
    val: boolean
  ) => void | React.Dispatch<React.SetStateAction<boolean>>;
}

const Modal = ({
  id,
  isOpen: isOpenProps,
  children,
  isLoading,
  fullHeight,
  closeModal,
  showDragHeader = true,
  dragHeaderColor,
  direction = 'bottom',
  bottomMargin,
  headerIcon,
  showCloseIcon = true,
  isScrollable = true,
}: Props) => {
  const { currentModal } = useGlobals();

  // ----- iOS/Safari: use visualViewport para lidar com barras/teclado -----
  const getVH = () => (typeof window !== 'undefined'
    ? (window.visualViewport?.height ?? window.innerHeight)
    : 0);

  const currentClosed = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [translateY, setTranslateY] = useState<number>(() => getVH() * 2);

  const velocity = useRef(0);
  const lastTime = useRef(0);
  const lastTouchY = useRef(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeUnmountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const animationClose = (force?: boolean, ignoreClose?: boolean) => {
    if (isLoading) return;

    const threshold = 150;
    const velocityThreshold = 10;

    if (translateY > threshold || velocity.current > velocityThreshold || force) {
      setTranslateY(getVH());
      if (closeAnimationTimeoutRef.current) clearTimeout(closeAnimationTimeoutRef.current);
      closeAnimationTimeoutRef.current = setTimeout(() => {
        if (!ignoreClose) closeModal(false);
        closeAnimationTimeoutRef.current = null;
      }, 300);
    } else {
      setTranslateY(0);
    }

    setTouchStart(null);
    setIsDragging(false);
    velocity.current = 0;
  };

  const handleClose = () => {
    currentClosed.current = true;
    animationClose(true)
  }

  const handleStart = (clientY: number) => {
    if (isLoading) return;
    setTouchStart(clientY);
    lastTouchY.current = clientY;
    lastTime.current = Date.now();
    setIsDragging(true);
  };

  const handleMove = (clientY: number) => {
    if (!touchStart || !isDragging || isLoading) return;

    const currentTime = Date.now();
    const deltaTime = currentTime - lastTime.current;
    const deltaY = clientY - touchStart;

    if (deltaTime > 0) {
      velocity.current = deltaY / deltaTime;
    }

    lastTouchY.current = clientY;
    lastTime.current = currentTime;

    if (deltaY > 0) {
      setTranslateY(deltaY);
    }
  };

  const handleEnd = async () => {
    if (!touchStart || isLoading) return;

    if (isDragging) {
      animationClose();
    }

    setIsDragging(false);
  };

  useEffect(() => {
    if (openAnimationTimeoutRef.current) {
      clearTimeout(openAnimationTimeoutRef.current);
      openAnimationTimeoutRef.current = null;
    }
    if (isOpen) {
      setTranslateY(getVH() * 2);
      openAnimationTimeoutRef.current = setTimeout(() => {
        setTranslateY(0);
        openAnimationTimeoutRef.current = null;
      }, 300);
    } else {
      setTranslateY(getVH() * 2);
    }
  }, [isOpen]);

  // Reagir a mudanças do viewport (barras do Safari, teclado, rotação)
  useEffect(() => {
    const onResize = () => {
      if (isOpen && !isDragging) {
        setTranslateY(0); // mantém o modal “ancorado”
      }
    };
    window.visualViewport?.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [isOpen, isDragging]);

  const handleDrag = {
    onMouseUp: handleEnd,
    onTouchEnd: handleEnd,
    onMouseLeave: handleEnd,
    onMouseMove: (e: MouseEvent) => handleMove(e.clientY),
    onMouseDown: (e: MouseEvent) => handleStart(e.clientY),
    onTouchMove: (e: TouchEvent) => handleMove(e.targetTouches[0].clientY),
    onTouchStart: (e: TouchEvent) => handleStart(e.targetTouches[0].clientY),
  };

  useEffect(() => {
    if (isOpenProps) {
      setIsOpen(isOpenProps);
      currentClosed.current = false;
      return
    }

    animationClose(true, false)
    if (closeUnmountTimeoutRef.current) clearTimeout(closeUnmountTimeoutRef.current);
    closeUnmountTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      closeUnmountTimeoutRef.current = null;
    }, currentClosed.current ? 0 : 400);
  }, [isOpenProps]);

  useEffect(() => {
    return () => {
      if (closeAnimationTimeoutRef.current) {
        clearTimeout(closeAnimationTimeoutRef.current);
        closeAnimationTimeoutRef.current = null;
      }
      if (openAnimationTimeoutRef.current) {
        clearTimeout(openAnimationTimeoutRef.current);
        openAnimationTimeoutRef.current = null;
      }
      if (closeUnmountTimeoutRef.current) {
        clearTimeout(closeUnmountTimeoutRef.current);
        closeUnmountTimeoutRef.current = null;
      }
    };
  }, []);

  const showDragAndClose = !currentModal || currentModal === id;

  return (
    <S.ModalContainer
      className={`modal-content ${isOpen && 'modal-content-open'}`}
    >
      <S.BgContainer
        onClick={handleClose}
        $isopen={isOpen}
        direction={direction}
        {...handleDrag}
        data-test={`modal-component-film-close-${id}`}
      />
      <S.ModalContent
        ref={modalRef}
        $bottomMargin={bottomMargin}
        $fullHeight={fullHeight}
        $isopen={isOpen}
        direction={direction}
        $showOverflow={headerIcon ? true : null}
        style={{
          transform: `translate(-50%, ${translateY}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {!isLoading && showDragHeader && showDragAndClose && (
          <S.DragHandleContent {...handleDrag} $dragHeaderColor={dragHeaderColor} />
        )}

        {!isLoading && headerIcon && (
          <S.HeaderIcon {...handleDrag}>
            {headerIcon}
          </S.HeaderIcon>
        )}

        {!isLoading && showCloseIcon && showDragAndClose && (
          <S.Header {...handleDrag}>
            <S.CloseIcon
              size={32}
              onClick={handleClose}
              data-test={`modal-component-button-close-${id}`}
            />
          </S.Header>
        )}

        {!isLoading && showDragAndClose && (
          <S.ModalDrag {...handleDrag}>
            <S.DragIcon />
          </S.ModalDrag>
        )}

        <S.ModalBody $fullHeight={fullHeight} $isScrollable={isScrollable}>
          {children}
        </S.ModalBody>
      </S.ModalContent>
    </S.ModalContainer>
  );
};

export default Modal;
